import { Icon, TooltipWrapper, Flex } from "dash-ui";
import { useTranslation } from "react-i18next";
import { getRecurringPeriodLabel } from "../SubscriptionChangeConfirmationModalV2.helpers";
import { formatDateString } from "pages/ProfilePage/components/ProfileBilling/ProfileBilling.helpers";
import { RecurringTypesEnum } from "dash-common";
import {
  InvoiceCoupon,
  TrialUpgradeDetails,
} from "../SubscriptionChangeConfirmationModalV2.interfaces";

interface PricingBreakdownProps {
  displayName: string;
  recurringType: RecurringTypesEnum | string;
  planPrice: number;
  creditAmount: number;
  discountAmount: number;
  coupon?: InvoiceCoupon | null;
  trial?: TrialUpgradeDetails;
}

export default function PricingBreakdown({
  displayName,
  recurringType,
  planPrice,
  creditAmount,
  discountAmount,
  coupon,
  trial,
}: PricingBreakdownProps) {
  const { t } = useTranslation("pages", {
    keyPrefix: "profilePage.billing.trialUpgrade",
  });
  const { t: tc } = useTranslation(["pages", "common"]);
  const periodLabel = getRecurringPeriodLabel(recurringType, tc);
  const trialEndDate = trial ? formatDateString(trial.endDate) : "";

  const rowPadding = trial ? "py-2" : "py-3";

  const creditRow = creditAmount < 0 && (
    <div className={`flex justify-between ${rowPadding}`}>
      <div className="flex flex-col">
        <div className="flex items-center gap-1">
          <span className="text-sm text-gray-500">
            {trial
              ? t("creditRow")
              : tc("pages:profilePage.billing.changeConfirm.unusedCredit")}
          </span>
          <TooltipWrapper
            content={
              <div style={{ maxWidth: "200px" }}>
                {trial
                  ? t("creditTooltip", { date: trialEndDate })
                  : tc(
                      "pages:profilePage.billing.changeConfirm.unusedCreditTooltip"
                    )}
              </div>
            }
            mt={-1.5}
            ml={-2}
          >
            <Flex mt={0} ml={3}>
              <Icon name="infoCircle" size={14} color="#94a3b8" />
            </Flex>
          </TooltipWrapper>
        </div>
        {trial && (
          <span className="text-xs text-gray-400">{t("creditCaption")}</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm text-green-600">
          -${Math.abs(creditAmount).toFixed(2)}
        </span>
        {!trial && (
          <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded">
            {tc("common:labels.applied")}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div className={trial ? "mb-3" : "mb-5"}>
      {trial ? (
        <>
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-500">
              {t("trialRow", { plan: displayName, days: trial.days })}
            </span>
            <span className="text-sm text-gray-900 font-semibold">$0.00</span>
          </div>
          {creditRow}
          <div className="flex justify-between py-2 border-t border-gray-100">
            <span className="text-sm text-gray-500">
              {t("thenRow", { date: trialEndDate })}
            </span>
            <span className="text-sm text-gray-900 font-semibold">
              {t(
                recurringType === RecurringTypesEnum.YEAR
                  ? "pricePerYear"
                  : "pricePerMonth",
                { price: planPrice.toFixed(2) }
              )}
            </span>
          </div>
        </>
      ) : (
        <>
          <div className="flex justify-between py-3 border-b border-gray-100">
            <span className="text-sm text-gray-500">
              {tc("pages:profilePage.billing.changeConfirm.planRow", {
                plan: displayName,
                period: periodLabel,
              })}
            </span>
            <span className="text-sm text-gray-900 font-semibold">
              ${planPrice.toFixed(2)}
            </span>
          </div>
          {creditRow}
        </>
      )}

      {discountAmount > 0 && coupon && (
        <div className="flex justify-between py-3">
          <div className="flex items-center gap-1">
            <span className="text-sm text-gray-500">
              {tc("pages:profilePage.billing.changeConfirm.discount", {
                name: coupon.name,
              })}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-green-600">
              -${discountAmount.toFixed(2)}
            </span>
            <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded">
              {tc("common:labels.applied")}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
